import { useState, useEffect, useCallback } from 'react';

import { ClockCountDirection } from 'src/types/matchControl/clock';

const useTimer = (interval: number, initialTime: number, initialDirection: ClockCountDirection) => {
  if (interval < 10) {
    throw new Error('Interval must be at least 10 milliseconds.');
  }

  const [timerData, setTimerData] = useState<{ time: number; startedAt: number | null }>({
    time: initialTime,
    startedAt: null,
  });
  const [direction] = useState<ClockCountDirection>(initialDirection);

  const { time, startedAt } = timerData;

  const calculateTime = useCallback(() => {
    if (startedAt === null) return initialTime;

    const now = Date.now();
    const elapsed = now - startedAt;
    return direction === 'up' ? initialTime + elapsed : initialTime - elapsed;
  }, [startedAt, direction, initialTime]);

  const updateTimer = useCallback(() => {
    if (startedAt === null) return;

    const time = calculateTime();
    setTimerData((prevState) => ({ ...prevState, time }));
  }, [calculateTime, startedAt]);

  useEffect(() => {
    updateTimer();
    const timerInterval = setInterval(updateTimer, interval);

    return () => clearInterval(timerInterval);
  }, [interval, updateTimer, startedAt]);

  const setTimer = (time: number, dateTime: Date | null) => {
    setTimerData({
      time,
      startedAt: dateTime ? dateTime.getTime() : null,
    });
  };

  return { time, setTimer };
};

export { useTimer };
